import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '../../../services/api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker - use unpkg CDN which is more reliable
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Use API URL base (without /api) for assets, or fallback to localhost:5555
const getAssetBaseUrl = () => {
  if (process.env.REACT_APP_ASSET_BASE_URL) {
    return process.env.REACT_APP_ASSET_BASE_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  return 'http://localhost:5555';
};
const ASSET_BASE_URL = getAssetBaseUrl();
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function IndemnityFormPublic() {
  const { slug } = useParams();
  const location = useLocation();
  const isStandalonePage = location.pathname.endsWith('/indemnity-form') && !location.pathname.startsWith('/campaign/');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState(null);
  const [indemnityForm, setIndemnityForm] = useState(null);
  const [form, setForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [theme, setTheme] = useState(null);
  
  // Form data states
  const [formData, setFormData] = useState({});
  const [hasReadCampaign, setHasReadCampaign] = useState(false);
  const [hasConfirmedDeclaration, setHasConfirmedDeclaration] = useState(false);
  const [dateSigned, setDateSigned] = useState('');
  const [wantsPromotionalMaterial, setWantsPromotionalMaterial] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  
  // Submission states
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  // PDF Viewer component for mobile
  const PDFViewer = ({ url }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageWidth, setPageWidth] = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          // Get the actual container width, accounting for padding
          const containerWidth = containerRef.current.offsetWidth;
          setPageWidth(containerWidth);
        } else {
          // Fallback: account for form padding (p-8 = 32px each side) and container padding (px-4 = 16px each side)
          // Total horizontal padding: 32px * 2 (form) + 16px * 2 (container) = 96px
          setPageWidth(window.innerWidth - 96);
        }
      };

      // Initial width calculation
      updateWidth();

      // Update on resize
      const handleResize = () => {
        updateWidth();
      };
      
      window.addEventListener('resize', handleResize);
      
      // Use ResizeObserver for more accurate container width tracking
      let resizeObserver;
      if (containerRef.current && window.ResizeObserver) {
        resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }, []);

    return (
      <div 
        ref={containerRef}
        className="w-full overflow-x-auto -mx-2 px-2"
        style={{ 
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Document
          file={url}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          loading={
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme?.primary_color || '#0F766E' }}></div>
            </div>
          }
          error={
            <div className="text-center py-8 text-red-500">
              Failed to load PDF. Please try again.
            </div>
          }
        >
          {numPages && pageWidth && Array.from({ length: numPages }, (_, i) => (
            <div key={i + 1} className="mb-4 flex justify-center">
              <Page 
                pageNumber={i + 1} 
                width={pageWidth}
                className="shadow-md"
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: theme?.primary_color || '#0F766E' }}></div>
                  </div>
                }
              />
            </div>
          ))}
        </Document>
      </div>
    );
  };

  const fetchIndemnityForm = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const endpoint = isStandalonePage 
        ? `/public/${slug}/indemnity-form`
        : `/public/campaigns/${slug}/indemnity-form`;
      const response = await api.get(endpoint);
      const data = response.data;

      if (!data.indemnityForm || !data.indemnityForm.is_active) {
        setError('Indemnity form not found or not active');
        setLoading(false);
        return;
      }

      setCampaign(data.campaign);
      setIndemnityForm(data.indemnityForm);
      setForm(data.form);
      setTheme(data.theme);
      
      // Debug: Log background image URL
      console.log('Indemnity Form Data:', data.indemnityForm);
      console.log('Background Image URL:', data.indemnityForm?.background_image_url);
      
      // Parse JSON field options if they're strings
      const parsedFields = (data.formFields || []).map(field => ({
        ...field,
        field_options: typeof field.field_options === 'string' 
          ? JSON.parse(field.field_options) 
          : field.field_options
      }));
      setFormFields(parsedFields);

      // Initialize form data
      const initialData = {};
      parsedFields.forEach(field => {
        if (field.field_type === 'checkbox') {
          initialData[field.field_name] = [];
        } else {
          initialData[field.field_name] = '';
        }
      });
      // Add system fields
      initialData.name = '';
      initialData.email = '';
      setFormData(initialData);

      setLoading(false);
    } catch (err) {
      console.error('Fetch indemnity form error:', err);
      setError('Failed to load indemnity form');
      setLoading(false);
    }
  }, [slug, isStandalonePage]);

  useEffect(() => {
    fetchIndemnityForm();
  }, [fetchIndemnityForm]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCheckboxChange = (fieldName, option, checked) => {
    setFormData(prev => {
      const currentValues = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
      if (checked) {
        return {
          ...prev,
          [fieldName]: [...currentValues, option]
        };
      } else {
        return {
          ...prev,
          [fieldName]: currentValues.filter(v => v !== option)
        };
      }
    });
  };

  const validateForm = () => {
    // Validate system fields
    if (!formData.name || !formData.name.trim()) {
      return 'Please enter your full name';
    }
    if (!formData.email || !formData.email.trim()) {
      return 'Please enter your email address';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Please enter a valid email address';
    }

    // Validate custom fields
    for (const field of formFields) {
      if (field.is_required) {
        const value = formData[field.field_name];
        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
          return `Please fill in: ${field.field_label}`;
        }
      }
    }

    // Validate indemnity-specific fields
    if (!hasReadCampaign) {
      return `Please confirm that you have read the "${campaign?.name || 'Campaign'}"`;
    }
    if (!hasConfirmedDeclaration) {
      return 'Please confirm that the information provided is true and correct';
    }
    if (!dateSigned) {
      return 'Please select the date signed';
    }
    
    // Validate that date is not in the future
    const selectedDate = new Date(dateSigned);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today
    if (selectedDate > today) {
      return 'Date signed cannot be in the future';
    }
    
    if (!recaptchaValue) {
      return 'Please complete the reCAPTCHA verification';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setSubmissionError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setSubmissionError('');

      const submitEndpoint = isStandalonePage
        ? `/public/${slug}/indemnity-form/submit`
        : `/public/campaigns/${slug}/indemnity-form/submit`;
      await api.post(submitEndpoint, {
        formData,
        hasReadCampaign,
        hasConfirmedDeclaration,
        dateSigned,
        wantsPromotionalMaterial,
        recaptchaToken: recaptchaValue
      });

      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      console.error('Submit error:', err);
      setSubmissionError(err.response?.data?.error || 'Failed to submit form. Please try again.');
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'date':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <input
              type={field.field_type === 'email' ? 'email' : field.field_type === 'date' ? 'date' : 'text'}
              value={formData[field.field_name] || ''}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
              style={{ 
                borderColor: theme?.primary_color || '#0F766E',
                '--tw-ring-color': theme?.primary_color || '#0F766E',
                ...(field.field_type === 'date' ? {
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  colorScheme: 'light',
                  backgroundColor: 'white',
                  color: '#111827',
                  minHeight: '48px',
                  height: '48px',
                  lineHeight: '1.5'
                } : {})
              }}
              placeholder={field.field_placeholder || ''}
              required={field.is_required}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={formData[field.field_name] || ''}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
              rows={field.field_options?.rows || 4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
              style={{ 
                borderColor: theme?.primary_color || '#0F766E',
                '--tw-ring-color': theme?.primary_color || '#0F766E'
              }}
              placeholder={field.field_placeholder || ''}
              required={field.is_required}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <select
              value={formData[field.field_name] || ''}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
              style={{ 
                borderColor: theme?.primary_color || '#0F766E',
                '--tw-ring-color': theme?.primary_color || '#0F766E'
              }}
              required={field.is_required}
            >
              <option value="">Select an option</option>
              {field.field_options?.options?.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );

      case 'radio':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options?.map((option, idx) => (
                <label key={idx} className="flex items-center">
                  <input
                    type="radio"
                    name={field.field_name}
                    value={option}
                    checked={formData[field.field_name] === option}
                    onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
                    className="mr-2"
                    required={field.is_required}
                  />
                  <span style={{ color: theme?.text_color || '#000' }}>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options?.map((option, idx) => (
                <label key={idx} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={(formData[field.field_name] || []).includes(option)}
                    onChange={(e) => handleCheckboxChange(field.field_name, option, e.target.checked)}
                    className="mr-2"
                  />
                  <span style={{ color: theme?.text_color || '#000' }}>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'phone':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <PhoneInput
              country={'au'}
              value={formData[field.field_name] || ''}
              onChange={(value) => handleFieldChange(field.field_name, value)}
              containerClass="w-full"
              inputClass="w-full"
              inputStyle={{ width: '100%' }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme?.background_color || '#F3F4F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: theme?.primary_color || '#0F766E' }}></div>
          <p className="text-lg" style={{ color: theme?.text_color || '#000' }}>Loading indemnity form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme?.background_color || '#F3F4F6' }}>
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold mb-4" style={{ color: theme?.text_color || '#000' }}>Error</h1>
          <p className="text-lg" style={{ color: theme?.text_color || '#666' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme?.background_color || '#F3F4F6' }}>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-4" style={{ color: theme?.text_color || '#000' }}>
            {form?.success_message || 'Thank you for your submission!'}
          </h1>
          <p className="text-lg" style={{ color: theme?.text_color || '#666' }}>
            Your indemnity form has been successfully submitted.
          </p>
        </div>
      </div>
    );
  }

  const backgroundImageUrl = indemnityForm?.background_image_url;
  const backgroundImageStyle = backgroundImageUrl
    ? {
        backgroundImage: `url(${ASSET_BASE_URL}${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: theme?.background_color || '#F3F4F6'
      }
    : { backgroundColor: theme?.background_color || '#F3F4F6' };
  
  // Debug: Log background image style
  if (backgroundImageUrl) {
    console.log('Background Image Style:', backgroundImageStyle);
    console.log('Full Background Image URL:', `${ASSET_BASE_URL}${backgroundImageUrl}`);
  }

  return (
    <div className="min-h-screen" style={backgroundImageStyle}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Campaign Logo */}
        {theme?.logo_url && (
          <div className="text-center mb-8">
            <img
              src={`${ASSET_BASE_URL}${theme.logo_url}`}
              alt={campaign?.name || 'Campaign Logo'}
              className="mx-auto h-20 object-contain"
            />
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4" style={{ color: theme?.primary_color || '#0F766E' }}>
            {campaign?.name || 'Indemnity Form'}
          </h1>
          {form?.description && (
            <p className="text-lg" style={{ color: theme?.text_color || '#666' }}>
              {form.description}
            </p>
          )}
        </div>

        {/* Form */}
        <form 
          onSubmit={handleSubmit} 
          className={`rounded-2xl shadow-xl p-8 space-y-6 ${
            backgroundImageUrl 
              ? 'bg-white/95 backdrop-blur-sm' 
              : 'bg-white'
          }`}
        >
          {/* System Fields */}
          <div>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
              style={{ 
                borderColor: theme?.primary_color || '#0F766E',
                '--tw-ring-color': theme?.primary_color || '#0F766E'
              }}
              required
            />
          </div>

          <div>
            <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
              style={{ 
                borderColor: theme?.primary_color || '#0F766E',
                '--tw-ring-color': theme?.primary_color || '#0F766E'
              }}
              required
            />
          </div>

          {/* Custom Form Fields */}
          {formFields.map(field => renderField(field))}

          {/* Waiver PDF/Image */}
          {indemnityForm?.waiver_image_url && (
            <div className="border-t pt-6">
              {indemnityForm.waiver_image_url.toLowerCase().endsWith('.pdf') ? (
                <div className="w-full space-y-4">
                  {/* Desktop: Show embedded iframe */}
                  <div className="hidden md:block">
                    <iframe
                      src={`${ASSET_BASE_URL}${indemnityForm.waiver_image_url}#toolbar=0&navpanes=0&scrollbar=0`}
                      className="w-full rounded-lg"
                      style={{ minHeight: '600px', border: 'none' }}
                      title="Waiver Document"
                    />
                  </div>
                  
                  {/* Mobile: Use react-pdf viewer */}
                  <div className="md:hidden">
                    <PDFViewer url={`${ASSET_BASE_URL}${indemnityForm.waiver_image_url}`} />
                  </div>
                </div>
              ) : (
                <img
                  src={`${ASSET_BASE_URL}${indemnityForm.waiver_image_url}`}
                  alt="Waiver"
                  className="w-full rounded-lg"
                />
              )}
            </div>
          )}

          {/* Indemnity Checkboxes */}
          <div className="border-t pt-6 space-y-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={hasReadCampaign}
                onChange={(e) => setHasReadCampaign(e.target.checked)}
                className="mt-1 mr-3"
                required
              />
              <span style={{ color: theme?.text_color || '#000' }}>
                I have read the "{campaign?.name || 'Campaign Name'}" <span className="text-red-500">*</span>
              </span>
            </label>

            <label className="flex items-start">
              <input
                type="checkbox"
                checked={hasConfirmedDeclaration}
                onChange={(e) => setHasConfirmedDeclaration(e.target.checked)}
                className="mt-1 mr-3"
                required
              />
              <span style={{ color: theme?.text_color || '#000' }}>
                Confirmation - I hereby declare that the information provided is true and correct <span className="text-red-500">*</span>
              </span>
            </label>

            <div>
              <label className="block text-base font-medium mb-2" style={{ color: theme?.text_color || '#000' }}>
                Date Signed <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateSigned}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  if (selectedDate > today) {
                    setSubmissionError('Date signed cannot be in the future');
                    return;
                  }
                  setDateSigned(selectedDate);
                  setSubmissionError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
                style={{ 
                  borderColor: theme?.primary_color || '#0F766E',
                  '--tw-ring-color': theme?.primary_color || '#0F766E',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  colorScheme: 'light',
                  backgroundColor: 'white',
                  color: '#111827',
                  minHeight: '48px',
                  height: '48px',
                  lineHeight: '1.5'
                }}
                required
              />
              {submissionError && submissionError.includes('Date signed') && (
                <p className="text-red-500 text-sm mt-1">{submissionError}</p>
              )}
            </div>

            <label className="flex items-start">
              <input
                type="checkbox"
                checked={wantsPromotionalMaterial}
                onChange={(e) => setWantsPromotionalMaterial(e.target.checked)}
                className="mt-1 mr-3"
              />
              <span style={{ color: theme?.text_color || '#000' }}>
                I would like to receive promotional material from Good Drinks Australia Pty Ltd
              </span>
            </label>
          </div>

          {/* Disclaimer */}
          {indemnityForm?.disclaimer_text && (
            <div 
              className="border-t pt-6 text-sm disclaimer-content"
              style={{ color: theme?.text_color || '#666' }}
              dangerouslySetInnerHTML={{ __html: indemnityForm.disclaimer_text }}
            />
          )}

          {/* reCAPTCHA */}
          <div className="flex justify-center">
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={setRecaptchaValue}
            />
          </div>

          {/* Error Message */}
          {submissionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {submissionError}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 px-6 text-white font-semibold rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: theme?.primary_color || '#0F766E' }}
          >
            {submitting ? 'Submitting...' : form?.submit_button_text || 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default IndemnityFormPublic;